/**
 * Admin V3 — Booking incident creation modal (AV3 A-4)
 *
 * Operator reports an incident against a booking via
 * POST /admin/control-tower/incidents (RBAC: admin:bookings:incident).
 *
 * Reuses the canonical Control Tower incident authority verbatim: the backend
 * links the incident to the booking, pushes the INCIDENT_REPORTED timeline
 * event, and logs the operator action. Parity with the legacy V2 IncidentModal.
 * No new business logic, no backend change.
 */
import React, { useState } from 'react';
import { Loader2, X, AlertTriangle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const inputCls =
  'w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 ' +
  'placeholder:text-slate-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100';

// Severity vocabulary is fixed by the backend schema
// (severity: ^(low|medium|high|critical)$). Labels localised.
const SEVERITIES = ['low', 'medium', 'high', 'critical'];

const BookingIncidentCreateModal = ({ open, bookingId, booking, incidentTypes = [], onClose, onDone, locale = DEFAULT_LOCALE }) => {
  const m = useAdminMutation('/admin/control-tower/incidents', 'post');
  const { showSuccess, showError } = useToast();

  const [incidentType, setIncidentType] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [description, setDescription] = useState('');

  if (!open || !bookingId) return null;

  const valid = Boolean(incidentType) && Boolean(description.trim());

  const submit = () => {
    m.mutate(
      {
        booking_id: bookingId,
        incident_type: incidentType,
        description: description.trim(),
        severity,
      },
      {
        onSuccess: () => {
          showSuccess(t('bookings.incident.ok', locale));
          if (onDone) onDone();
          onClose();
        },
        onError: (e) => showError(e?.response?.data?.detail || t('bookings.incident.error', locale)),
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="adminv3-booking-incident-modal">
      <div className="w-[520px] max-w-[94vw] rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-[17px] font-semibold text-slate-900">{t('bookings.incident.title', locale)}</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="adminv3-booking-incident-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-3 text-[12px] text-slate-500">
          {t('bookings.incident.description', locale).replace('{ref}', booking?.booking_reference || '')}
        </p>

        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800" data-testid="adminv3-booking-incident-audit">
          {t('bookings.incident.auditWarning', locale)}
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[12px] font-semibold text-slate-600">{t('bookings.incident.typeLabel', locale)}</label>
            <select
              value={incidentType}
              onChange={(e) => setIncidentType(e.target.value)}
              className={inputCls}
              data-testid="adminv3-booking-incident-type"
            >
              <option value="">{t('bookings.incident.typePlaceholder', locale)}</option>
              {incidentTypes.map((it) => (
                <option key={it.value} value={it.value}>{it.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[12px] font-semibold text-slate-600">{t('bookings.incident.severityLabel', locale)}</label>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              className={inputCls}
              data-testid="adminv3-booking-incident-severity"
            >
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>{t(`bookings.incident.severity.${s}`, locale)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[12px] font-semibold text-slate-600">{t('bookings.incident.descriptionLabel', locale)}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className={inputCls}
              data-testid="adminv3-booking-incident-description"
            />
          </div>
        </div>

        {!valid && (
          <p className="mt-3 text-[12px] text-rose-600" data-testid="adminv3-booking-incident-missing">
            {t('bookings.incident.required', locale)}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="adminv3-booking-incident-dismiss">
            {t('bookings.incident.cancel', locale)}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={submit}
            disabled={!valid || m.loading}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="adminv3-booking-incident-confirm"
          >
            {m.loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
            {t('bookings.incident.confirm', locale)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingIncidentCreateModal;
